import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import {
  BedDouble, Bath, Car, Maximize, MapPin, MessageCircle, Phone, ArrowLeft, Loader2, ImageOff, Tag,
} from 'lucide-react';
import { fetchProperty, brl, whatsappLink } from './portalApi';
import type { PropertyDetail, SiteInfo } from './portalApi';

export default function PortalProperty() {
  const { id } = useParams();
  const { site } = useOutletContext<{ site: SiteInfo | null }>();
  const [p, setP] = useState<PropertyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchProperty(id)
      .then((d) => {
        setP(d);
        setActive(0);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="py-32 flex items-center justify-center text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (notFound || !p) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="text-lg font-medium text-gray-700">Imóvel não encontrado ou indisponível.</p>
        <Link to="/imoveis" className="mt-4 inline-flex items-center gap-1 text-primary-600 font-semibold">
          <ArrowLeft className="w-4 h-4" /> Voltar para a busca
        </Link>
      </div>
    );
  }

  const isRent = p.rentPrice != null && p.rentPrice > 0;
  const price = isRent ? p.rentPrice : p.salePrice;
  const photos = p.photos.length ? p.photos : p.cover ? [p.cover] : [];
  const wa = whatsappLink(
    site?.whatsapp,
    `Olá! Tenho interesse no imóvel ${p.code} - ${p.title} (${brl(price)}). Pode me passar mais informações?`,
  );
  const specs = [
    p.bedrooms != null && { icon: BedDouble, label: 'Quartos', value: p.bedrooms },
    p.bathrooms != null && { icon: Bath, label: 'Banheiros', value: p.bathrooms },
    p.parkingSpaces != null && { icon: Car, label: 'Vagas', value: p.parkingSpaces },
    p.usableArea != null && p.usableArea > 0 && { icon: Maximize, label: 'Área útil', value: `${p.usableArea} m²` },
    p.builtArea != null && p.builtArea > 0 && { icon: Maximize, label: 'Área construída', value: `${p.builtArea} m²` },
    p.totalArea != null && p.totalArea > 0 && { icon: Maximize, label: 'Área total', value: `${p.totalArea} m²` },
  ].filter(Boolean) as { icon: any; label: string; value: any }[];

  const chars: string[] = Array.isArray(p.characteristics)
    ? p.characteristics
    : p.characteristics && typeof p.characteristics === 'object'
      ? Object.entries(p.characteristics)
          .filter(([, v]) => v === true || (typeof v === 'string' && v))
          .map(([k, v]) => (v === true ? k : `${k}: ${v}`))
      : [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Link to="/imoveis" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary-700">
        <ArrowLeft className="w-4 h-4" /> Voltar para a busca
      </Link>

      <div className="mt-4 grid lg:grid-cols-3 gap-8">
        {/* Galeria + descrição */}
        <div className="lg:col-span-2">
          <div className="relative aspect-[16/10] bg-gray-100 rounded-2xl overflow-hidden">
            {photos[active] ? (
              <img src={photos[active]} alt={p.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <ImageOff className="w-12 h-12" />
              </div>
            )}
            <span
              className={`absolute top-4 left-4 text-sm font-semibold px-3 py-1 rounded-full text-white shadow ${
                isRent ? 'bg-emerald-600' : 'bg-primary-600'
              }`}
            >
              {isRent ? 'Locação' : 'Venda'}
            </span>
          </div>

          {photos.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {photos.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 ${
                    i === active ? 'border-primary-600' : 'border-transparent'
                  }`}
                >
                  <img src={src} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500">
              <Tag className="w-3.5 h-3.5" /> {p.typeLabel} · Cód. {p.code}
            </span>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold text-gray-900">{p.title}</h1>
            <p className="mt-1 text-gray-500 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {[p.street && `${p.street}${p.number ? `, ${p.number}` : ''}`, p.neighborhood, p.city, p.state]
                .filter(Boolean)
                .join(' - ')}
            </p>

            {specs.length > 0 && (
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {specs.map((s) => (
                  <div key={s.label} className="bg-gray-50 rounded-xl p-4 text-center">
                    <s.icon className="w-6 h-6 mx-auto text-primary-600" />
                    <p className="mt-1 font-semibold text-gray-800">{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {p.description && (
              <div className="mt-8">
                <h2 className="text-lg font-bold text-gray-900">Descrição</h2>
                <p className="mt-2 text-gray-600 whitespace-pre-line leading-relaxed">{p.description}</p>
              </div>
            )}

            {chars.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-bold text-gray-900">Características</h2>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {chars.map((c) => (
                    <li key={c} className="flex items-center gap-2 text-gray-600 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500" /> {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {p.latitude != null && p.longitude != null && (
              <div className="mt-8">
                <h2 className="text-lg font-bold text-gray-900">Localização</h2>
                <div className="mt-3 rounded-2xl overflow-hidden border border-gray-200 aspect-[16/9]">
                  <iframe
                    title="Mapa do imóvel"
                    className="w-full h-full"
                    loading="lazy"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${p.longitude - 0.004}%2C${p.latitude - 0.003}%2C${p.longitude + 0.004}%2C${p.latitude + 0.003}&layer=mapnik&marker=${p.latitude}%2C${p.longitude}`}
                  />
                </div>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${p.latitude}&mlon=${p.longitude}#map=17/${p.latitude}/${p.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-sm text-primary-600 hover:text-primary-700"
                >
                  Ver no mapa ampliado →
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Card de preço/contato */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
            <p className="text-sm text-gray-500">{isRent ? 'Valor do aluguel' : 'Valor de venda'}</p>
            <p className="text-3xl font-bold text-primary-700">
              {brl(price)}
              {isRent && <span className="text-base font-normal text-gray-500">/mês</span>}
            </p>

            <ul className="mt-4 space-y-1 text-sm text-gray-600">
              {p.condoFee != null && p.condoFee > 0 && (
                <li className="flex justify-between"><span>Condomínio</span><span>{brl(p.condoFee)}</span></li>
              )}
              {p.iptu != null && p.iptu > 0 && (
                <li className="flex justify-between"><span>IPTU</span><span>{brl(p.iptu)}</span></li>
              )}
            </ul>

            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              className="mt-6 flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
            >
              <MessageCircle className="w-5 h-5" /> Falar no WhatsApp
            </a>
            {site?.phone && (
              <a
                href={`tel:${site.phone}`}
                className="mt-2 flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
              >
                <Phone className="w-5 h-5" /> {site.phone}
              </a>
            )}
            <p className="mt-4 text-xs text-gray-400 text-center">
              {site?.name}{site?.creci ? ` · CRECI ${site.creci}` : ''}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
